// util
import { defineStore } from 'pinia';

/** 头部状态栏占位视觉收紧量（px），减轻手机上头部与内容间距过大 */
const STATUS_BAR_TIGHTEN_PX = 8

/** 二级页导航行占位（不含状态栏），原普遍用 80 导致头内容间距过大 */
export const PAGE_NAV_BAR_HEIGHT = 48

/**
 * 是否运行在微信小程序 web-view
 * @returns {boolean}
 */
function isInWxMiniProgram() {
  try {
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : '';
    const inWxBrowser = /micromessenger/i.test(String(ua).toLowerCase());
    if (!inWxBrowser) return false;
    if (typeof window !== 'undefined' && window.__wxjs_environment === 'miniprogram') return true;
    if (typeof wx !== 'undefined' && wx.miniProgram) return true;
    if (/\bminiProgram\b/i.test(String(ua))) return true;
  } catch (e) {
    // ignore
  }
  return false;
}

/**
 * 收敛状态栏高度，避免头部整体过高、与内容间距偏大
 * @param {number} height
 * @returns {number}
 */
function normalizeStatusBarHeight(height) {
  let h = Number(height);
  if (!h || isNaN(h) || h <= 0) return 0;
  // 原生偶发返回物理像素：按 DPR 折算到 CSS 像素
  if (typeof window !== 'undefined' && window.devicePixelRatio > 1.5 && h > 56) {
    const scaled = Math.round(h / window.devicePixelRatio);
    if (scaled >= 16 && scaled < h) h = scaled;
  }
  // 上限保护（状态栏一般不超过 54）
  if (h > 54) h = 54;
  return Math.max(0, Math.round(h - STATUS_BAR_TIGHTEN_PX));
}

/**
 * 获取安全的状态栏高度
 * 优先级：原生获取 > CSS环境变量 > 默认值
 * @returns {Number} 状态栏高度（px）
 */
function getSafeStatusBarHeight() {
  // 0. 微信小程序 web-view：统一固定 10px（按产品要求）
  if (isInWxMiniProgram()) {
    return 10;
  }

  // 1. 尝试从 CSS 环境变量获取（支持刘海屏）
  if (typeof window !== 'undefined' && window.getComputedStyle) {
    const root = document.documentElement;
    const safeAreaTop = getComputedStyle(root).getPropertyValue('--safe-area-inset-top');
    
    if (safeAreaTop) {
      const height = parseInt(safeAreaTop, 10);
      if (!isNaN(height) && height > 0) {
        console.log('从CSS环境变量获取状态栏高度:', height);
        return normalizeStatusBarHeight(height);
      }
    }
  }
  
  // 2. 根据设备类型设置默认值（已含收紧）
  const userAgent = navigator.userAgent;
  
  // iPhone X 及以上（刘海屏）
  if (/iPhone/.test(userAgent)) {
    const isIPhoneX = /iPhone X|iPhone 1[0-9]|iPhone 1[0-9] Pro/.test(userAgent) || 
                      (window.screen.height >= 812 && window.screen.width >= 375);
    if (isIPhoneX) {
      console.log('检测到iPhone刘海屏，使用收紧后高度');
      return normalizeStatusBarHeight(44);
    }
    console.log('检测到普通iPhone，使用收紧后高度');
    return normalizeStatusBarHeight(20);
  }
  
  // Android 设备
  if (/Android/.test(userAgent)) {
    console.log('检测到Android设备，使用收紧后高度');
    return normalizeStatusBarHeight(24);
  }
  
  // iPad
  if (/iPad/.test(userAgent)) {
    console.log('检测到iPad，使用收紧后高度');
    return normalizeStatusBarHeight(20);
  }
  
  // 默认值
  console.log('使用默认状态栏高度（收紧后）');
  return normalizeStatusBarHeight(24);
}

export const useGlobalStore = defineStore('global', {
  state: () => ({
    statusBarHeight: getSafeStatusBarHeight(), // 顶部状态栏高度（智能获取）
    keepAliveList: ['healthHomePage'] // 需要缓存的页面
  }),
  getters: {
    /**
     * 二级页固定导航行高度（不含状态栏），用于内容区顶距
     * @returns {number}
     */
    pageNavBarHeight() {
      return PAGE_NAV_BAR_HEIGHT
    },
    /**
     * 二级页内容区 paddingTop = 状态栏 + 导航行
     * @param {{ statusBarHeight: number }} state
     * @returns {number}
     */
    pageContentTop(state) {
      return Number(state.statusBarHeight || 0) + PAGE_NAV_BAR_HEIGHT
    },
    /**
     * 二级页返回按钮距顶（状态栏 + 导航内边距）
     * @param {{ statusBarHeight: number }} state
     * @returns {number}
     */
    pageBackBtnTop(state) {
      return Number(state.statusBarHeight || 0) + 8
    },
  },
  actions: {
    // 设置顶部状态栏高度
    setStatusBarHeight(height) {
      // 小程序环境下固定 10px，不接受外部覆盖
      if (isInWxMiniProgram()) {
        this.statusBarHeight = 10;
        return;
      }
      // 验证高度值的有效性
      if (height && !isNaN(height) && height > 0 && height < 200) {
        const next = normalizeStatusBarHeight(+height);
        console.log('设置状态栏高度:', height, '→', next);
        this.statusBarHeight = next;
      } else {
        console.warn('无效的状态栏高度值:', height, '使用当前值:', this.statusBarHeight);
      }
    },
    // 添加keepAlive项目
    addKeepAlive(name) {
      if (this.keepAliveList.includes(name)) {
        return;
      }
      const keepAliveList = [...this.keepAliveList];
      keepAliveList.push(name);
      this.keepAliveList = keepAliveList;
    },
    // 移除keepAlive项目
    removeKeepAlive(name) {
      if (!this.keepAliveList.includes(name)) {
        return;
      }
      const keepAliveList = [...this.keepAliveList];
      this.keepAliveList = keepAliveList.filter(item => item !== name);
    }
  }
})



// WEBPACK FOOTER //
// ./src/stores/global.js